<template>
  <div class="sw-toast-container">
    <transition-group name="sw-toast">
      <ToastItem v-for="toast in toasts" :key="toast.id" v-bind="toast" @close="removeToast(toast.id)" />
    </transition-group>
  </div>
</template>

<script>
import ToastItem from './ToastItem.vue'

export default {
  name: 'ToastContainer',
  components: { ToastItem },
  data() {
    return {
      toasts: []
    }
  },
  methods: {
    addToast(toast) {
      this.toasts = this.toasts.filter(t => !(t.title === toast.title && t.description === toast.description))
      this.toasts.push(toast)
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(t => t.id !== id)
    },
    clearAll() {
      this.toasts = []
    },
    hasToast(id) {
      return this.toasts.some(t => t.id === id)
    }
  }
}
</script>

<style lang="scss">
.sw-toast-container {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 700px;
  width: calc(100% - 32px);
  pointer-events: none;

  > span {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

// Base toast
.sw-toast {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 12px;
  border-radius: 10px;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: -0.4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
}

.sw-toast__icon {
  width: 18px;
  flex-shrink: 0;
  margin-top: 1px;

  &.material-icons {
    font-size: 18px;
  }
}

.sw-toast__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.sw-toast__title {
  display: block;
  font-weight: 700;
}

.sw-toast__description {
  display: block;
  font-weight: 400;
}

.sw-toast__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid var(--color-neutral-200);
  border-radius: 6px;
  background: #ffffff;
  color: var(--color-neutral-900);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  align-self: center;

  .material-icons {
    font-size: 16px;
  }

  &:hover {
    background: var(--color-neutral-50);
  }
}

.sw-toast__close {
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  margin-top: 2px;
  opacity: 0.8;

  &.material-icons {
    font-size: 18px;
  }

  &:hover {
    opacity: 1;
  }
}

// Regular variant - solid backgrounds, white text
.sw-toast--regular {
  color: #ffffff;

  .sw-toast__close {
    color: #ffffff;
  }

  &.sw-toast--regular {
    background-color: #1d76ac;
  }

  &.sw-toast--info {
    background-color: #02a9ca;
  }

  &.sw-toast--success {
    background-color: #1db946;
  }

  &.sw-toast--warning {
    background-color: #f59e0b;
  }

  &.sw-toast--danger {
    background-color: #ef4444;
  }
}

// Container variant - light backgrounds, colored border + title
.sw-toast--container {
  border: 1px solid;
  background-color: #f2f9fd;
  color: #003e52;

  .sw-toast__description {
    color: var(--color-neutral-500);
  }

  .sw-toast__close {
    color: var(--color-neutral-500);
  }

  &.sw-toast--regular {
    background-color: var(--color-primary-50);
    border-color: #1d76ac;

    .sw-toast__icon,
    .sw-toast__title {
      color: var(--color-primary-700);
    }
  }

  &.sw-toast--info {
    background-color: #ecfeff;
    border-color: #02a9ca;

    .sw-toast__icon,
    .sw-toast__title {
      color: #135d77;
    }
  }

  &.sw-toast--success {
    background-color: #e9f9ed;
    border-color: #1db946;

    .sw-toast__icon,
    .sw-toast__title {
      color: #0d7227;
    }
  }

  &.sw-toast--warning {
    background-color: #fffbeb;
    border-color: #f59e0b;

    .sw-toast__icon,
    .sw-toast__title {
      color: #92400e;
    }
  }

  &.sw-toast--danger {
    background-color: #fef2f2;
    border-color: #ef4444;

    .sw-toast__icon,
    .sw-toast__title {
      color: #991b1b;
    }
  }
}

// Transitions
.sw-toast-enter-active {
  transition: all 0.3s ease;
}

.sw-toast-leave-active {
  transition: all 0.2s ease;
}

.sw-toast-enter,
.sw-toast-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

.sw-toast-move {
  transition: transform 0.3s ease;
}

// Mobile
@media only screen and (max-width: 768px) {
  .sw-toast-container {
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    padding: 0 16px;
  }
}
</style>
